import { z } from "zod";

import { MEMBERSHIP_STATUSES } from "./membership";

/**
 * Kullanıcılar ve Üyelikler yüzeyinin sözleşmesi (CLOS-189 `[051]`).
 *
 * ### Kartın çekirdek ayrımı: ÜÇ kayıt türü, ÜÇ yaşam döngüsü
 *
 * *"Kullanıcı hesabı profili, organizasyon üyeliği ve davet üç ayrı kayıt türü ve yaşam
 * döngüsü olarak gösterilir"* ve *"kullanıcı hesabı kapatılması, bir organizasyon
 * üyeliğinin sona ermesiyle AYNI İŞLEM DEĞİLDİR."*
 *
 * Bu yüzden burada üç ayrı durum kümesi var ve hiçbiri diğerine indirgenmiyor: hesap
 * (`ACCOUNT_STATUSES`), üyelik (`MEMBERSHIP_STATUSES`, `./membership`) ve davet
 * (`INVITATION_STATUSES`). Tek bir "durum" alanında birleştirilseydi *"hesabı kapandı"* ile
 * *"üyeliği sonlandırıldı"* ayırt edilemez olurdu — yöneticinin atacağı adım ikisinde
 * tamamen farklı.
 */

/**
 * Hesap yaşam döngüsü (CLOS-282 `[014]`).
 *
 * ⚠ Küme sunucudan BURAYA taşındı: arayüz hesap durumunu göstermek zorunda ve etiketleri
 * sayfada yazsaydık aynı kümenin ikinci bir kopyası doğardı (kriter 5). Sunucu tarafı
 * (`apps/api/src/lib/accountStatus.ts`) artık bu kümeyi yeniden dışa veriyor; geçiş
 * matrisi ve yazma yolu orada kalıyor — burası yalnız KİMLİK ve GÖRÜNEN AD.
 */
export const ACCOUNT_STATUSES = {
  /** Normal hesap. */
  active: "active",
  /** Güvenlik açısından kısıtlı: yeni oturum açılamaz, ürün yüzeyi kapalıdır. */
  securityRestricted: "security_restricted",
  /** Kapanma süreci: oturum açılabilir ama YENİ işlem yapılamaz. */
  closing: "closing",
  /** Terminal: hesap kapatıldı (CLOS-69 `[049]`). Bir SİLME değil — satırlar yerinde. */
  closed: "closed",
} as const;

export type AccountStatus = (typeof ACCOUNT_STATUSES)[keyof typeof ACCOUNT_STATUSES];

/**
 * Yöneticiye görünen hesap durumu adları.
 *
 * *"Kapanıyor"* ile *"Kapatıldı"* ayrı: birinden dönülebilir, diğerinden dönülemez ve
 * yöneticinin bekleyip beklemeyeceği buna bağlı.
 */
export const ACCOUNT_STATUS_NAMES: Record<AccountStatus, string> = {
  active: "Aktif",
  security_restricted: "Güvenlik kısıtlı",
  closing: "Kapanıyor",
  closed: "Kapatıldı",
};

/**
 * Davet yaşam döngüsü (CLOS-73/74/75 `[034]`–`[036]`).
 *
 * Kart dördünü de ayrı sayıyor: *"bekleyen, reddedilmiş, iptal edilmiş ve süresi dolmuş."*
 * `accepted` beşincisi ve listede kalıyor — kabul edilmiş bir davet artık bir üyeliktir ama
 * geçmişi silmek, *"bu kişiyi kim çağırdı?"* sorusunu cevapsız bırakırdı.
 */
export const INVITATION_STATUSES = [
  "pending",
  "accepted",
  "rejected",
  "cancelled",
  "expired",
] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const INVITATION_STATUS_NAMES: Record<InvitationStatus, string> = {
  pending: "Bekliyor",
  accepted: "Kabul edildi",
  rejected: "Reddedildi",
  cancelled: "İptal edildi",
  expired: "Süresi doldu",
};

/**
 * Üyeliğin bağlı olduğu departmanlar (CLOS-189 `[051][S05]`).
 *
 * Üye satırına GÖMÜLÜ, ayrı bir uç değil: departman bilgisi listeyle her zaman birlikte
 * isteniyor ve ayrı uç, üye başına bir gidiş dönüş (N+1) demek olurdu.
 */
export const memberDepartmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /**
   * ANA DEPARTMAN mı (CLOS-1218 — Departmanlar Kanonu §5.2)?
   *
   * ⚠ Bir NİTELİK, ayrı bir liste değil. "Ana departmanlar" ve "diğerleri" diye iki dizi
   * dönseydik, kanonun tek bir üyelik ilişkisi olarak tanımladığı şey istemcide iki ayrı
   * kavrama bölünürdü.
   */
  isPrimary: z.boolean(),
});
export type MemberDepartment = z.infer<typeof memberDepartmentSchema>;

/**
 * Üye satırı — `GET /api/tenants/:id/members`.
 *
 * ⚠ `status` ÜYELİĞİN, `accountStatus` HESABIN durumu ve ikisi bağımsız değişir. Hesabı
 * kapanan kişinin üyeliği `active` kalıyor; iki alanı tek alana indirmek kartın en açık
 * ayrımını yok ederdi.
 */
export const directoryMemberSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  name: z.string(),
  email: z.string(),
  status: z.enum(MEMBERSHIP_STATUSES),
  roleId: z.uuid().nullable(),
  roleName: z.string().nullable(),
  accountStatus: z.enum(ACCOUNT_STATUSES),
  statusReason: z.string().nullable(),
  statusChangedAt: z.string().nullable(),
  createdAt: z.string(),
  /**
   * ⚠ Satır içi tanım DEĞİL, `memberDepartmentSchema`. İlk hâlinde burada ayrı bir nesne
   * şeması yazılıydı ve `isPrimary` eklenirken ikisi sessizce ayrıştı: sunucu alanı
   * gönderiyor, sözleşme onu bilmiyordu. Tek kaynak, tek şekil.
   */
  departments: z.array(memberDepartmentSchema),
});
export type DirectoryMember = z.infer<typeof directoryMemberSchema>;

/** Davet satırı — `GET /api/tenants/:id/invites`. Token TAŞIMIYOR (`[034]` kararı). */
export const directoryInvitationSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  roleId: z.uuid().nullable(),
  roleName: z.string(),
  status: z.enum(INVITATION_STATUSES),
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type DirectoryInvitation = z.infer<typeof directoryInvitationSchema>;

/**
 * Kapasite özeti — `GET /api/tenants/:id/capacity`.
 *
 * Rezervasyonlar üyelerden AYRI sayılıyor: bekleyen bir davet koltuğu tutuyor ama henüz
 * bir üye değil. Tek sayıda birleştirilseydi yönetici *"neden 12 kişi görüyorum ama 14
 * koltuk dolu?"* sorusunun cevabını hiçbir yerde bulamazdı.
 */
export const capacitySummarySchema = z.object({
  members: z.number().int().nonnegative(),
  reservations: z.number().int().nonnegative(),
  used: z.number().int().nonnegative(),
  /** `null` = sınırsız. */
  limit: z.number().int().nonnegative().nullable(),
  canInvite: z.boolean(),
  blockedReason: z.enum(["no_entitlement", "limit_reached"]).nullable(),
});
export type CapacitySummary = z.infer<typeof capacitySummarySchema>;

/**
 * Kapasite engelinin kullanıcıya söylenecek hâli (kart: *"kapasite ... durumları ayrı ve
 * TUTARLI gösterilir"*).
 *
 * İki engel iki farklı adım gerektiriyor ve tek bir *"davet edemezsiniz"* cümlesi ikisini
 * de gizlerdi: koltuk dolmuşsa yönetici birini çıkarabilir, lisans hak vermiyorsa
 * çıkarmanın hiçbir faydası yok.
 */
export const CAPACITY_BLOCK_MESSAGES: Record<
  NonNullable<CapacitySummary["blockedReason"]>,
  string
> = {
  limit_reached: "Koltuk sınırına ulaşıldı. Yeni davet için bir üyeliği sonlandırın.",
  no_entitlement: "Mevcut lisansınız yeni kullanıcı davetine izin vermiyor.",
};

/**
 * SÖZLEŞME BAĞLAMINDAKİ İÇ ADAY — `GET /api/contracts/:id/internal-candidates`.
 *
 * ### ⚠ NEDEN `DirectoryMember` DEĞİL
 *
 * Üye dizini bir YÖNETİM ekranının verisi: e-posta, rol, hesap durumu, koltuk geçmişi.
 * Sözleşme seçicisinin sorusu daha dar — *"bu sözleşmeye kimi atayabilirim?"* — ve cevabı
 * yönetim yetkisi istememeli. Aynı tipi paylaşsaydık, sözleşme yetkisiyle okunan uç
 * kaçınılmaz olarak dizinin bütün alanlarını taşırdı.
 *
 * ⚠ Liste YALNIZ AKTİF üyelikleri taşıyor; çağıran ayrıca süzmez.
 */
export const internalCandidateSchema = z.object({
  membershipId: z.uuid(),
  userId: z.uuid(),
  name: z.string(),
});
export type InternalCandidate = z.infer<typeof internalCandidateSchema>;

/**
 * SÖZLEŞME BAĞLAMINDAKİ İÇ ADAY SORGUSU (Mega E2E BUG-009/009b).
 *
 * ⚠ YETENEK İSTEĞE BAĞLI: bazı seçiciler "organizasyonda kim var" diye soruyor (iç sorumlu,
 * görev devri), bazıları "bu adımı kim YAPABİLİR" diye (onaylayıcı, inceleyici, imzacı).
 * Parametre verilmezse liste süzülmez; verilirse sunucu kapının kendi kuralıyla süzer.
 *
 * ⚠ WHITELIST: istemci serbest metin gönderemiyor. Liste ile kapı aynı anahtar kümesini
 * konuşmazsa, seçici bir gün kapının tanımadığı bir yetenek için aday üretir.
 */
export const internalCandidateQuerySchema = z.object({
  capability: z
    .enum(
      ["contract.approve", "contract.review", "contract.sign", "contract.reconcile"],
      "Geçerli bir yetenek seçilmelidir",
    )
    .optional(),
});
